/**
 * Role Management Page
 *
 * Displays the 5-tier role hierarchy with permission assignments.
 * Platform operators can view system roles and create custom roles.
 *
 * Uses @aaimplatform/ui components and openapi-fetch typed client.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Card } from '@aaimplatform/ui';
import { createRbacClient } from '@aaimplatform/institutional-types/api-clients';
import type { components } from '@aaimplatform/institutional-types/rbac-management';
import { CreateRoleModal } from '../components/CreateRoleModal';
import { formatApiError } from '../lib/api-error';

type Role = components['schemas']['Role'];

const TIER_LABELS: Record<number, string> = {
  0: 'Tier 0: APEX (Platform-wide)',
  1: 'Tier 1: Core Processor (Multi-tenant)',
  2: 'Tier 2: Tenant (Single FI)',
  3: 'Tier 3: Consumer (Individual)',
  4: 'Tier 4: Delegate (Delegated access)',
};

export function RolesPage() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTier, setSelectedTier] = useState<number | null>(null);
  const [showSystemOnly, setShowSystemOnly] = useState<boolean | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const client = createRbacClient();

  useEffect(() => {
    fetchRoles();
  }, [selectedTier, showSystemOnly]);

  async function fetchRoles() {
    try {
      setLoading(true);
      setError(null);

      const { data, error: apiError, response } = await client.GET('/api/v1/roles', {
        params: {
          query: {
            ...(selectedTier !== null && { tier: selectedTier }),
            ...(showSystemOnly !== null && { isSystem: showSystemOnly }),
            includePermissions: false,
          },
        },
      });

      if (apiError) {
        setError(formatApiError(apiError, 'Failed to load roles', response?.status));
        return;
      }

      if (data && 'success' in data && data.success && 'data' in data) {
        setRoles(data.data || []);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error');
    } finally {
      setLoading(false);
    }
  }

  const rolesByTier = roles.reduce((acc, role) => {
    if (!acc[role.tier]) acc[role.tier] = [];
    acc[role.tier].push(role);
    return acc;
  }, {} as Record<number, Role[]>);

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-secondary">Loading roles...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className="mb-6">
        <p className="text-secondary text-sm">
          5-tier role hierarchy with permission inheritance. System roles are seeded
          and cannot be deleted. Custom roles can be created for tenant-specific needs.
        </p>
      </div>

      {/* Actions and Filters */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
          {/* Tier Filter */}
          <div>
            <label className="text-xs text-tertiary mr-2">Tier:</label>
            <div className="inline-flex gap-1">
              {[0, 1, 2, 3, 4].map((tier) => (
                <button
                  key={tier}
                  onClick={() => setSelectedTier(selectedTier === tier ? null : tier)}
                  className={`px-3 py-1 text-xs rounded transition-colors ${
                    selectedTier === tier
                      ? 'bg-accent text-white'
                      : 'bg-surface border border-recessed text-secondary hover:border-accent'
                  }`}
                >
                  T{tier}
                </button>
              ))}
            </div>
          </div>

          {/* System/Custom Filter */}
          <div>
            <label className="text-xs text-tertiary mr-2">Type:</label>
            <div className="inline-flex gap-1">
              <button
                onClick={() => setShowSystemOnly(null)}
                className={`px-3 py-1 text-xs rounded transition-colors ${
                  showSystemOnly === null
                    ? 'bg-accent text-white'
                    : 'bg-surface border border-recessed text-secondary hover:border-accent'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setShowSystemOnly(true)}
                className={`px-3 py-1 text-xs rounded transition-colors ${
                  showSystemOnly === true
                    ? 'bg-accent text-white'
                    : 'bg-surface border border-recessed text-secondary hover:border-accent'
                }`}
              >
                System
              </button>
              <button
                onClick={() => setShowSystemOnly(false)}
                className={`px-3 py-1 text-xs rounded transition-colors ${
                  showSystemOnly === false
                    ? 'bg-accent text-white'
                    : 'bg-surface border border-recessed text-secondary hover:border-accent'
                }`}
              >
                Custom
              </button>
            </div>
          </div>

          <div className="text-sm text-tertiary">{roles.length} role{roles.length !== 1 ? 's' : ''}</div>
        </div>

        <Button variant="primary" onClick={() => setCreateModalOpen(true)}>
          Create Custom Role
        </Button>
      </div>

      {/* Error State */}
      {error && (
        <Alert type="error" title="Error" className="mb-6">
          {error}
        </Alert>
      )}

      {/* Roles by Tier */}
      {roles.length === 0 ? (
        <Card>
          <div className="p-8 text-center">
            <div className="text-secondary mb-4">
              {selectedTier !== null || showSystemOnly !== null
                ? `No roles match the selected filters`
                : 'No roles found - database may need seeding'}
            </div>
            <p className="text-sm text-tertiary mb-6">
              {selectedTier === null && showSystemOnly === null
                ? 'Run seed-rbac.ts to populate 16 system roles with 59 permissions.'
                : 'Try adjusting your filters to see more roles.'}
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-8">
          {Object.entries(rolesByTier)
            .sort(([a], [b]) => Number(a) - Number(b))
            .map(([tier, tierRoles]) => (
              <div key={tier}>
                <h2 className="font-sans text-base font-medium mb-4 text-secondary">
                  {TIER_LABELS[Number(tier)]}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {tierRoles.map((role) => (
                    <Card key={role.id} className="hover:shadow-md transition-shadow">
                      <Link to={`/roles/${role.id}`} className="block p-4">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-sans text-sm font-medium flex-1 mr-2">{role.name}</h3>
                          <div className="flex gap-1">
                            {role.isSystem && (
                              <span className="text-xs text-tertiary bg-surface px-2 py-0.5 rounded whitespace-nowrap">
                                System
                              </span>
                            )}
                            {role.providerFamily && (
                              <span className="text-xs text-accent bg-accent/10 px-2 py-0.5 rounded whitespace-nowrap">
                                {role.providerFamily}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-xs text-secondary mb-2">{role.displayName}</div>
                        {role.permissions && role.permissions.length > 0 && (
                          <div className="text-xs text-tertiary">
                            {role.permissions.length} permission{role.permissions.length !== 1 ? 's' : ''}
                          </div>
                        )}
                      </Link>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
        </div>
      )}

      {/* Create Modal */}
      <CreateRoleModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={(newRole) => {
          setRoles([...roles, newRole]);
          setCreateModalOpen(false);
        }}
      />
    </div>
  );
}
